import dayjs from 'dayjs'

import { HandPiece, Piece, PlayerColor } from 'features/board/board'
import { ValidSkewedWallPositions, ValidWallPositions } from 'features/board/utils'
import { HexId } from 'features/hex-board/hex-board'
import { DEFAULT_RATING } from 'features/user/elo-rating'

export type InitializeMetadata = { walls: [ValidWallPositions, ValidSkewedWallPositions] }
export type CrownMetadata = { piece: HandPiece; hexId: HexId }
export type DeployMetadata = { piece: Piece; hexId: HexId }
export type MoveMetadata = { from: HexId; to: HexId }
export type SwitchMetadata = { from: HexId; to: HexId }
export type WithdrawMetadata = { player: PlayerColor; hexId: HexId }
export type DrawMetadata = { player: PlayerColor; eventCode: 'request' | 'accept' | 'reject' }

export type Clock = {
  clock: Record<PlayerColor, number>
}

export type PossibleMove =
  | { type: 'phaseTransition' | 'fight'; metadata: null }
  | { type: 'initialize'; metadata: InitializeMetadata }
  | { type: 'distribute'; metadata: DeployMetadata }
  | { type: 'deploy'; metadata: DeployMetadata }
  | { type: 'move'; metadata: MoveMetadata }
  | { type: 'switch'; metadata: SwitchMetadata }
  | { type: 'withdraw'; metadata: WithdrawMetadata }
  | { type: 'draw'; metadata: DrawMetadata }

export type PossibleMoves = PossibleMove[]

export type GameEvent = PossibleMove & Clock

export type GameEventType = GameEvent['type']

export interface GameResult {
  winner: PlayerColor | 'draw'
  reason: 'fight' | 'resign' | 'draw' | 'repetition' | 'timeout' | 'abort'
}

export interface Game {
  gameId: string
  events: GameEvent[]
  status: 'pending' | 'playing' | 'finished'
  timeToPlay: number
  players: Record<PlayerColor, string | null>
  createdAt: string
  ratings: {
    before: Record<PlayerColor, number>
    after?: Record<PlayerColor, number>
  }
  result?: GameResult
  ai?: PlayerColor
  autoAddAI?: boolean
}

export interface Player {
  avatar: string
  displayName: string
  email: string
  online: boolean
  nickname?: string
  rating?: number
}

export const initialGameState: Game = {
  gameId: '',
  events: [],
  status: 'pending',
  timeToPlay: 900000,
  createdAt: dayjs().toISOString(),
  players: {
    white: null,
    black: null,
  },
  ratings: {
    before: {
      white: DEFAULT_RATING,
      black: DEFAULT_RATING,
    },
  },
}

export interface DatabaseGame extends Omit<Game, 'events'> {
  events: Record<string, GameEvent[]>
}
